/* global SideDrawer, initLazyImages */

class BdColorFacet {
  static param = 'filter.v.m.product_details.color';
  static catalogs = new Map();
  static selectionParam = 'bd_color';
  static groupAttribute = 'data-bd-color-groups';
  static idPrefix = 'bd-color';
  static label(value) { return value; }
  static compare(a, b) { return a.localeCompare(b); }

  static families(label) {
    return [...new Set(label.split(/[,/;&]+/).map((part) => {
      const words = part.trim().toLowerCase().replace(/grey/g, 'gray').split(/\s+/);
      const family = words.find((word) => ['brown', 'beige', 'black', 'gray', 'white', 'red', 'tan', 'natural', 'multi', 'blue', 'green', 'gold', 'yellow', 'orange', 'pink'].includes(word));
      const value = family || words.join(' ');
      return value ? value[0].toUpperCase() + value.slice(1) : '';
    }).filter(Boolean))];
  }

  constructor(form, search) {
    this.form = form;
    this.params = new URLSearchParams(search);
    this.detail = form.querySelector(`details[data-filter-param="${this.constructor.param}"]`);
    if (!this.detail) return;
    this.inputs = [...this.detail.querySelectorAll(`input[name="${this.constructor.param}"]`)];
    const context = new URLSearchParams();
    ['q', 'type', 'options[prefix]'].forEach((key) => {
      if (this.params.has(key)) context.set(key, this.params.get(key));
    });
    this.catalogKey = `${window.location.pathname}?${context}`;
    const options = this.inputs.map((input) => ({
      value: input.value,
      label: input.labels[0]?.querySelector('[data-filter-value-label]')?.textContent || input.value
    }));
    const narrowed = [...this.params.keys()].some((key) => key.startsWith('filter.') && key !== this.constructor.param);
    if (!narrowed) this.constructor.catalogs.set(this.catalogKey, options);
    this.selected = new Set(this.params.getAll(this.constructor.selectionParam));
    if (!this.params.has(this.constructor.param)) this.selected.clear();
    this.setCatalog(this.constructor.catalogs.get(this.catalogKey) || options);
    if (narrowed && !this.constructor.catalogs.has(this.catalogKey) && form.dataset.filterSectionId) {
      this.ready = this.loadCatalog(context);
    }
  }

  setCatalog(options) {
    this.groups = new Map();
    options.forEach((option) => {
      const input = this.inputs.find((item) => item.value === option.value) || { value: option.value, disabled: true, checked: false };
      this.constructor.families(option.label).forEach((family) => {
        if (!this.groups.has(family)) this.groups.set(family, []);
        this.groups.get(family).push(input);
      });
    });
    this.render();
  }

  async loadCatalog(context) {
    this.detail.querySelectorAll(`input[name="${this.constructor.selectionParam}"]`).forEach((input) => { input.disabled = true; });
    try {
      const url = new URL(window.location.pathname, window.location.origin);
      url.search = context.toString();
      url.searchParams.set('section_id', this.form.dataset.filterSectionId);
      const response = await fetch(url);
      if (!response.ok) throw new Error('Filter options unavailable');
      const document = new DOMParser().parseFromString(await response.text(), 'text/html');
      const options = [...document.querySelectorAll(`input[name="${this.constructor.param}"]`)].map((input) => ({
        value: input.value,
        label: input.labels[0]?.querySelector('[data-filter-value-label]')?.textContent || input.value
      }));
      if (!options.length) throw new Error('Filter options unavailable');
      this.constructor.catalogs.set(this.catalogKey, options);
      if (this.detail.isConnected) this.setCatalog(options);
    } catch (error) {
      if (!this.detail.isConnected) return;
      this.detail.querySelector(`[${this.constructor.groupAttribute}]`)?.remove();
      this.inputs.forEach((input) => {
        input.closest('li').hidden = false;
        input.closest('li').classList.remove('js-hidden');
      });
      console.warn(error);
    }
  }

  writeSelection(params, selected) {
    params.delete(this.constructor.selectionParam);
    params.delete(this.constructor.param);
    const values = new Set();
    selected.forEach((family) => {
      params.append(this.constructor.selectionParam, family);
      this.groups.get(family)?.forEach((input) => values.add(input.value));
    });
    values.forEach((value) => params.append(this.constructor.param, value));
    params.delete('page');
    return params;
  }

  preserveSelectionLinks(root) {
    if (!root || !this.selected.size) return;
    root.querySelectorAll('a[href]').forEach((link) => {
      if (link.matches('.js-clear-all') || link.dataset.filterParam === this.constructor.param || link.closest(`details[data-filter-param="${this.constructor.param}"]`)) return;
      const url = new URL(link.href);
      if (url.origin !== window.location.origin || url.pathname !== window.location.pathname || !url.searchParams.has(this.constructor.param)) return;
      const page = url.searchParams.get('page');
      this.writeSelection(url.searchParams, this.selected);
      if (page) url.searchParams.set('page', page);
      link.href = url.href;
    });
  }

  render() {
    this.detail.querySelector(`[${this.constructor.groupAttribute}]`)?.remove();
    const list = document.createElement('ul');
    list.setAttribute(this.constructor.groupAttribute, '');
    list.setAttribute('role', 'list');
    [...this.groups].sort(([a], [b]) => this.constructor.compare(a, b)).forEach(([family, members]) => {
      const li = document.createElement('li');
      const input = document.createElement('input');
      input.type = 'checkbox';
      input.className = 'checkbox visually-hidden focus-label-before';
      input.id = `${this.constructor.idPrefix}-${encodeURIComponent(family)}`;
      input.name = this.constructor.selectionParam;
      input.value = family;
      input.checked = this.selected.has(family);
      input.indeterminate = !this.selected.size && members.some((member) => member.checked);
      input.disabled = !input.checked && members.every((member) => member.disabled);
      const label = document.createElement('label');
      label.htmlFor = input.id;
      label.className = 'filter__label flex items-start w-full m-0';
      label.textContent = this.constructor.label(family);
      li.append(input, label);
      list.append(li);
    });
    this.inputs.forEach((input) => { input.closest('li').hidden = true; });
    this.detail.querySelector('.filter__content').append(list);
    this.detail.querySelector('.filter__more')?.remove();
    const clear = this.detail.querySelector('.js-clear-filter');
    if (clear) {
      const url = new URL(clear.href);
      url.searchParams.delete(this.constructor.selectionParam);
      clear.href = url.href;
    }
    const clearAll = this.form.querySelector('.js-clear-all');
    if (clearAll) {
      const url = new URL(clearAll.href);
      url.searchParams.delete(this.constructor.selectionParam);
      clearAll.href = url.href;
    }
    if (!this.selected.size) return;
    this.preserveSelectionLinks(this.form);
    this.preserveSelectionLinks(document.getElementById('filter-results'));
    const chips = [...this.form.querySelectorAll(`.active-filter[data-filter-param="${this.constructor.param}"]`)];
    const template = chips[0];
    if (!template) return;
    const parent = template.parentElement;
    const icon = template.querySelector('svg');
    chips.forEach((chip) => chip.remove());
    this.selected.forEach((family) => {
      const chip = document.createElement('a');
      chip.className = template.className;
      chip.dataset.filterParam = this.constructor.param;
      const remaining = new Set(this.selected);
      remaining.delete(family);
      const url = new URL(window.location.href);
      url.search = this.writeSelection(new URLSearchParams(this.params), remaining).toString();
      chip.href = url.href;
      if (icon) chip.append(icon.cloneNode(true));
      chip.append(document.createTextNode(this.constructor.label(family)));
      parent.append(chip);
    });
    const heading = this.form.querySelector('.active-filters-header');
    if (heading) heading.textContent = this.form.dataset.appliedLabel;
  }

  sync(target) {
    if (!this.detail || target.name !== this.constructor.selectionParam) return;
    const selected = new Set([...this.detail.querySelectorAll(`input[name="${this.constructor.selectionParam}"]:checked`)].map((input) => input.value));
    const params = this.writeSelection(new URLSearchParams(), selected);
    const values = new Set(params.getAll(this.constructor.param));
    this.inputs.forEach((input) => {
      input.checked = values.has(input.value);
      input.disabled = false;
    });
  }
}

class BdWidthFacet extends BdColorFacet {
  static param = 'filter.v.m.product_dimensions.width_in_';
  static catalogs = new Map();
  static selectionParam = 'bd_width';
  static groupAttribute = 'data-bd-width-groups';
  static idPrefix = 'bd-width';

  static families(label) {
    const value = String(label).trim().replace(/_/g, '.').replace(/[″"]/g, '');
    if (!/^\d+(?:\.\d+)?$/.test(value)) return [];
    const width = Number(value);
    return Number.isFinite(width) && width >= 0 ? [String(Math.floor(width))] : [];
  }

  static label(value) { return value === '0' ? 'Under 1″' : `${value}″–${value}.99″`; }
  static compare(a, b) { return Number(a) - Number(b); }
}

if (!customElements.get('facet-filters')) {
  class FacetFilters extends SideDrawer {
    connectedCallback() {
      this.init();
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.historyChangeHandler);

      if (this.breakpointChangeHandler) {
        window.removeEventListener('on:breakpoint-change', this.breakpointChangeHandler);
      }
    }

    init() {
      this.filteringEnabled = this.dataset.filtering === 'true';
      this.sortingEnabled = this.dataset.sorting === 'true';
      this.form = document.getElementById('facets');
      this.results = document.getElementById('filter-results');
      this.expanded = [];
      this.filterChangeTimeout = null;

      this.handleBreakpointChange();
      this.addElements();
      this.addListeners();

      this.historyChangeHandler = this.historyChangeHandler || this.handleHistoryChange.bind(this);
      window.addEventListener('popstate', this.historyChangeHandler);
    }

    addElements(search = window.location.search) {
      if (this.filteringEnabled) {
        this.filters = this.querySelector('.facets__filters');
        this.activeFilters = this.querySelector('.facets__active-filters');
        this.activeFiltersList = this.querySelector('.active-filters');
        this.activeFiltersHeader = this.querySelector('.active-filters-header');
        this.footer = this.querySelector('.facets__footer');
        this.colorFacet = new BdColorFacet(this.form, search);
        this.widthFacet = new BdWidthFacet(this.form, search);
      }

      if (this.sortingEnabled) {
        this.mobileSortByOptions = this.querySelectorAll('.js-drawer-sort-by');
        this.desktopSortBy = document.querySelector('.products-toolbar__sort');
      }
    }

    addListeners() {
      if (this.filteringEnabled) {
        this.breakpointChangeHandler = this.breakpointChangeHandler
          || this.handleBreakpointChange.bind(this);
        this.filters.addEventListener('click', this.handleFiltersClick.bind(this));
        this.filters.addEventListener('input', this.handleFilterChange.bind(this));
        this.filters.addEventListener('change', this.handleFilterChange.bind(this));
        this.activeFilters.addEventListener('click', this.handleActiveFiltersClick.bind(this));
        window.addEventListener('on:breakpoint-change', this.breakpointChangeHandler);
      }

      if (this.sortingEnabled) {
        this.desktopSortBy.addEventListener('change', this.handleFilterChange.bind(this));
      }
    }

    /**
     * Handles viewport breakpoint changes.
     */
    handleBreakpointChange() {
      if (theme.mediaMatches.lg) {
        if (this.hasAttribute('open') && this.getAttribute('aria-modal') === 'true') {
          this.close();
        }
        this.setAttribute('open', '');
        this.setAttribute('aria-hidden', 'false');
        this.removeAttribute('aria-modal');
        this.removeAttribute('role');
      } else {
        this.close();
        this.setAttribute('role', 'dialog');
        this.setAttribute('aria-modal', 'true');
        this.setAttribute('aria-hidden', 'true');
        this.hidden = false;
      }
    }

    /**
     * Handles 'input' events on the filters and 'change' events on the sort by dropdown.
     * @param {object} evt - Event object.
     */
    handleFilterChange(evt) {
      // Only allow price 'change' events
      if (evt.type === 'change' && !(evt.target.id?.includes('price-range') || evt.target.id?.includes('sort-by'))) return;

      // Dont reload when typing a price
      if (evt.target.id?.includes('price-range') && evt.constructor.name === 'InputEvent') return;

      this.colorFacet?.sync(evt.target);
      this.widthFacet?.sync(evt.target);

      const timeoutDelay = 500;

      clearTimeout(this.filterChangeTimeout);

      this.filterChangeTimeout = setTimeout(() => {
        const formData = new FormData(this.form);
        const searchParams = new URLSearchParams(formData);
        if (this.colorFacet?.detail && searchParams.has('bd_color')) {
          this.colorFacet.writeSelection(searchParams, new Set(searchParams.getAll('bd_color')));
        }
        if (this.widthFacet?.detail && searchParams.has('bd_width')) {
          this.widthFacet.writeSelection(searchParams, new Set(searchParams.getAll('bd_width')));
        }
        const emptyParams = [];

        if (this.sortingEnabled) {
          let currentSortBy = searchParams.get('sort_by')
            || this.desktopSortBy?.querySelector('select[name="sort_by"]')?.value
            || new URLSearchParams(window.location.search).get('sort_by');

          // Keep the mobile facets form sync'd with the desktop sort by dropdown
          if (evt.target.tagName === 'CUSTOM-SELECT') {
            currentSortBy = evt.detail.selectedValue;
            this.mobileSortByOptions.forEach((option) => {
              option.checked = option.value === evt.detail.selectedValue;
            });
          }

          // Set the 'sort_by' parameter.
          if (currentSortBy && currentSortBy !== 'null' && currentSortBy !== 'undefined') {
            searchParams.set('sort_by', currentSortBy);
          } else {
            searchParams.delete('sort_by');
          }
        }

        // Get empty parameters.
        searchParams.forEach((value, key) => {
          if (!value) emptyParams.push(key);
        });

        // Remove empty parameters.
        emptyParams.forEach((key) => {
          searchParams.delete(key);
        });

        this.applyFilters(searchParams.toString(), evt);
      }, timeoutDelay);
    }

    /**
     * Handles 'click' events on the filters.
     * @param {object} evt - Event object.
     */
    handleFiltersClick(evt) {
      const { target } = evt;

      // Filter 'clear' button clicked.
      if (target.matches('.js-clear-filter')) {
        evt.preventDefault();
        this.applyFilters(new URL(evt.target.href).searchParams.toString(), evt);
      }

      // Filter 'show more' button clicked.
      if (target.matches('.js-show-more')) {
        const filter = target.closest('.filter');
        target.remove();

        filter.querySelectorAll('li').forEach((el) => {
          el.classList.remove('js-hidden');
        });

        if (!this.expanded.includes(filter.id)) {
          this.expanded.push(filter.id);
        }
      }
    }

    /**
     * Handles 'click' events on the active filters.
     * @param {object} evt - Event object.
     */
    handleActiveFiltersClick(evt) {
      const link = evt.target.closest('a');
      if (!link) return;
      evt.preventDefault();
      this.applyFilters(new URL(link.href).searchParams.toString(), evt);
    }

    /**
     * Handles history changes (e.g. back button clicked).
     * @param {object} evt - Event object.
     */
    handleHistoryChange(evt) {
      if (evt.state !== null) {
        let searchParams = '';

        if (evt.state && evt.state.searchParams) {
          ({ searchParams } = evt.state);
        }

        this.applyFilters(searchParams, null, false);
      }
    }

    /**
     * Fetches the filtered/sorted page data and updates the current page.
     * @param {string} searchParams - Filter/sort search parameters.
     * @param {object} evt - Event object.
     * @param {boolean} [updateUrl=true] - Update url with the selected options.
     */
    async applyFilters(searchParams, evt, updateUrl = true) {
      await Promise.all([this.colorFacet?.ready, this.widthFacet?.ready]);
      const normalizedParams = new URLSearchParams(searchParams);
      if (this.colorFacet?.detail && normalizedParams.has('bd_color') && normalizedParams.has(BdColorFacet.param)) {
        this.colorFacet.writeSelection(normalizedParams, new Set(normalizedParams.getAll('bd_color')));
        searchParams = normalizedParams.toString();
      }
      if (this.widthFacet?.detail && normalizedParams.has('bd_width') && normalizedParams.has(BdWidthFacet.param)) {
        this.widthFacet.writeSelection(normalizedParams, new Set(normalizedParams.getAll('bd_width')));
      }
      searchParams = normalizedParams.toString();
      try {
        // Preserve the current element focus
        const activeElementId = document.activeElement.id;

        // Disable infinite scrolling.
        const customPagination = document.querySelector('custom-pagination');
        if (customPagination) customPagination.dataset.pauseInfiniteScroll = 'true';

        // Set loading state.
        this.results.classList.add('is-loading');

        // Disable "Show X results" button until submission is complete.
        const closeBtn = this.querySelector('.js-close-drawer-mob');
        closeBtn.ariaDisabled = 'true';
        closeBtn.classList.add('is-loading');

        // Use Section Rendering API for the request, if possible.
        let fetchUrl = `${window.location.pathname}?${searchParams}`;
        if (this.form.dataset.filterSectionId) {
          fetchUrl += `&section_id=${this.form.dataset.filterSectionId}`;
        }

        // Cancel current fetch request. (Raises an exception)
        if (this.applyFiltersFetchAbortController) {
          this.applyFiltersFetchAbortController.abort('Request changed');
        }
        this.applyFiltersFetchAbortController = new AbortController();

        // Fetch filtered products markup.
        const response = await fetch(fetchUrl, {
          method: 'GET',
          signal: this.applyFiltersFetchAbortController.signal
        });

        if (response.ok) {
          const tmpl = document.createElement('template');
          tmpl.innerHTML = await response.text();

          // Restore UI state.
          this.form.querySelectorAll('details-disclosure > details').forEach((existingFilter) => {
            const target = tmpl.content.getElementById(existingFilter.id);
            if (target) {
              target.open = existingFilter.open;
            }
          });
          tmpl.content.querySelectorAll('#facets details-disclosure > details').forEach((newFilter) => {
            if (this.expanded.includes(newFilter.id)) {
              const hiddenElements = newFilter.querySelectorAll('.js-hidden');
              hiddenElements.forEach((listItem) => {
                listItem.classList.remove('js-hidden');
              });
              newFilter.querySelector('.filter__more')?.remove();
            }
          });

          // Update the filters.
          this.form.innerHTML = tmpl.content.getElementById('facets').innerHTML;

          // Update the label of the mobile filter button
          closeBtn.innerText = tmpl.content.querySelector('.js-close-drawer-mob').innerText;

          // Preserve the CSS class of the results
          const currentResultsUl = this.results.querySelector('ul');
          this.currentResultsClass = currentResultsUl ? this.results.querySelector('ul').getAttribute('class') : this.currentResultsClass;

          // Update the results.
          this.results.innerHTML = tmpl.content.getElementById('filter-results').innerHTML;

          // Set the CSS class of the results to what it was
          const newResultsUl = this.results.querySelector('ul');
          if (newResultsUl && this.currentResultsClass) newResultsUl.setAttribute('class', this.currentResultsClass);

          // Reinitialize re-rendered components.
          this.addElements(searchParams);
          this.addListeners();

          // Reinitialize lazyload images after filters applied.
          if (typeof initLazyImages === 'function') initLazyImages();

          // Reinitialize any custom pagination
          if (customPagination && customPagination.reload) customPagination.reload();

          // Update the URL.
          if (updateUrl) FacetFilters.updateURL(searchParams);

          // Scroll to the top of the results if needed
          if (this.results.getBoundingClientRect().top < 0) {
            // If the header is sticky, compensate for it when scrolling to elements
            let headerHeight = 0;
            if (document.querySelector('store-header[data-is-sticky="true"]')) {
              headerHeight = Number.parseInt(
                getComputedStyle(this.parentElement)
                  .getPropertyValue('--header-height')
                  .replace('px', ''),
                10
              ) || 0;
            }
            window.scrollTo({
              top: this.results.getBoundingClientRect().top + window.scrollY - headerHeight - 45,
              behavior: 'smooth'
            });
          }

          // Enable the "Show X results" button
          closeBtn.classList.remove('is-loading');
          closeBtn.removeAttribute('aria-disabled');

          // Renable infinite scroll
          if (customPagination) customPagination.dataset.pauseInfiniteScroll = 'false';

          // Focus on the element with the same ID in the new HTML
          if (activeElementId) document.getElementById(activeElementId)?.focus();

          // Broadcast the update for anything else to hook into
          document.dispatchEvent(new CustomEvent('on:facet-filters:updated'), { bubbles: true });
        }
      } catch (error) {
        console.warn(error);
      } finally {
        this.results.classList.remove('is-loading');
      }
    }

    /**
     * Updates the url with the current filter/sort parameters.
     * @param {string} searchParams - Filter/sort parameters.
     */
    static updateURL(searchParams) {
      window.history.pushState(
        { searchParams },
        '',
        `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`
      );
    }
  }

  customElements.define('facet-filters', FacetFilters);
}

